import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Avatar, Box, Button, Card, Icon, Tooltip } from '@node-space/ui-components'
import { PathNames } from 'constants/General'
import { PortfolioStatus } from 'types/rewards'
import { useRewardsSheetContext } from '../context/RewardsSheetProvider'
import { formatAmount } from '../utils'

export interface RewardsAssetProps {
  currency: string
  apyRate: string
  onDeposit?: () => void
}

const MOCKED_AVAILABLE_AMOUNT = 100000 // TODO: Remove this once the API is implemented

export const RewardsAsset = ({ currency, apyRate, onDeposit }: RewardsAssetProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { userPortfolios } = useRewardsSheetContext()

  const portfolioForAsset = userPortfolios?.find(
    portfolio => portfolio?.currency === currency && portfolio?.status === PortfolioStatus.ACTIVE
  )
  const hasAllocation = !!portfolioForAsset

  const handleSeeDetails = (currency: string) => {
    navigate(`${PathNames.REWARDS}/${currency}`)
  }

  return (
    <Card variant="interactive" full onClick={() => handleSeeDetails(currency)}>
      <Box flexCol gapY={8}>
        <Box flex align="center" justify="between" width="full">
          <Box flex align="center" gap={8}>
            <Avatar name={currency} size="small" />
            <h3 className="text-h3 text-foreground-prominent">{currency}</h3>
          </Box>

          {!hasAllocation && (
            <Tooltip bodyContent={t('rewards.allocateCurrency')}>
              <Button
                iconElement={<Icon name="Plus" size="large" />}
                variant="ghost"
                noPadding
                onClick={e => {
                  e.stopPropagation()
                  onDeposit?.()
                }}
                className="underline"
              />
            </Tooltip>
          )}
        </Box>

        <Box flex align="start" justify="between" width="full" gap={16}>
          <Box flexCol align="start">
            <p className="text-body-strong text-foreground-prominent">{apyRate}</p>
            <p className="text-body-regular text-foreground-subdued">{t('rewards.rewardsRate')}</p>
          </Box>

          <Box flexCol align="start">
            <p className="text-body-regular text-foreground-subdued">
              {hasAllocation
                ? t('rewards.totalAllocatedBalance')
                : t('rewards.availableToAllocate')}
            </p>
            <p className="text-body-strong text-foreground-prominent">
              {formatAmount(
                hasAllocation
                  ? Number.parseFloat(portfolioForAsset?.balance?.total ?? '0')
                  : MOCKED_AVAILABLE_AMOUNT
              )}
            </p>
          </Box>
        </Box>
      </Box>
    </Card>
  )
}
